import React, { useState } from 'react';
import { X } from 'lucide-react';
import type { ReportType } from '../types';
import { useUpload } from '../lib/useUpload';
import { Modal, Alert, UploadProgress, inputClass, labelClass, primaryBtn, secondaryBtn } from './ui';

export const REPORT_CATEGORIES = [
  'general',
  'task',
  'incident',
  'maintenance',
  'cleaning',
  'security',
  'electrical',
  'plumbing',
  'visitation',
  'other',
];

export const REPORT_TYPE_LABELS: Record<ReportType, string> = {
  daily: 'Daily',
  weekly: 'Weekly',
  monthly: 'Monthly',
  diesel: 'Diesel Log',
  ekedc: 'EKEDC',
  security_breach: 'Security Report',
  incident: 'Incident',
  residents_complaint: "Residents' Complaint",
  monthly_kpi: 'Monthly KPI',
  electrical: 'Electrical',
  plumbing: 'Plumbing',
  cleaning: 'Cleaning',
  pool: 'Swimming Pool',
  lift: 'Lift',
  generator: 'Generator',
  staff_report: 'Staff Report',
  residents_report: 'Residents Report',
  maintenance: 'Maintenance',
  security: 'Security',
  waste_disposal: 'Waste Disposal',
  fumigation: 'Fumigation',
  water_treatment: 'Water Treatment',
  sewage_maintenance: 'Sewage Maintenance',
  road_drainage: 'Road & Drainage Maintenance',
  common_area: 'Common Area',
  status_report: 'Status Report',
};

export const ALL_REPORT_TYPES: ReportType[] = [
  'daily', 'weekly', 'monthly', 'diesel', 'ekedc', 'security_breach', 'incident', 'residents_complaint', 'monthly_kpi',
  'electrical', 'plumbing', 'cleaning', 'pool', 'lift', 'generator', 'staff_report', 'residents_report', 'maintenance',
  'security', 'waste_disposal', 'fumigation', 'water_treatment', 'sewage_maintenance', 'road_drainage', 'common_area',
  'status_report',
];

interface Props {
  open: boolean;
  onClose: () => void;
  onSubmitted: () => void;
  title?: string;
  defaultType?: ReportType;
  allowedTypes?: ReportType[];
  showTypeSelect?: boolean;
  specialty?: string | null;
}

interface ResidentEntry {
  name: string;
  unit: string;
}

interface FormState {
  report_date: string;
  report_type: ReportType;
  category: string;
  heading: string;
  content: string;
  diesel_liters_remaining: string;
  ekedc_units_remaining: string;
  severity: string;
  security_location: string;
  action_taken: string;
  residents: ResidentEntry[];
}

export const specialtyDefaultType = (specialty?: string | null): ReportType | null => {
  if (!specialty) return null;
  const s = specialty.toLowerCase();
  if (s.includes('facility')) return 'diesel';
  if (s.includes('manager')) return 'monthly';
  if (s.includes('security')) return 'security_breach';
  if (s.includes('generator')) return 'diesel';
  return 'daily';
};

export const specialtyTypes = (specialty?: string | null): ReportType[] => {
  const s = (specialty || '').toLowerCase();
  if (s.includes('facility')) return ['daily', 'weekly', 'monthly', 'diesel', 'ekedc', 'security_breach', 'incident', 'electrical', 'plumbing', 'pool', 'cleaning', 'waste_disposal', 'fumigation', 'water_treatment', 'sewage_maintenance', 'road_drainage', 'common_area', 'status_report', 'residents_complaint'];
  if (s.includes('manager')) return ['monthly'];
  if (s.includes('security')) return ['daily', 'weekly', 'monthly', 'security_breach', 'incident', 'monthly_kpi'];
  if (s.includes('generator')) return ['daily', 'weekly', 'monthly', 'generator', 'diesel', 'incident', 'monthly_kpi'];
  if (s.includes('electric')) return ['daily', 'weekly', 'monthly', 'electrical', 'incident', 'monthly_kpi'];
  if (s.includes('plumb')) return ['daily', 'weekly', 'monthly', 'plumbing', 'incident', 'monthly_kpi'];
  if (s.includes('pool')) return ['daily', 'weekly', 'monthly', 'cleaning', 'incident', 'monthly_kpi'];
  if (s.includes('lift')) return ['daily', 'weekly', 'monthly', 'lift', 'incident', 'monthly_kpi'];
  return ['daily', 'weekly', 'monthly'];
};

export default function ReportFormModal({
  open,
  onClose,
  onSubmitted,
  title = 'New Report',
  defaultType,
  allowedTypes,
  showTypeSelect = true,
  specialty,
}: Props) {
  const auto = defaultType || specialtyDefaultType(specialty);
  const [form, setForm] = useState<FormState>({
    report_date: new Date().toISOString().slice(0, 10),
    report_type: auto || 'daily',
    category: 'general',
    heading: '',
    content: '',
    diesel_liters_remaining: '',
    ekedc_units_remaining: '',
    severity: '',
    security_location: '',
    action_taken: '',
    residents: [{ name: '', unit: '' }],
  });
  const [files, setFiles] = useState<File[]>([]);
  const [error, setError] = useState('');
  const upload = useUpload();

  const types = allowedTypes && allowedTypes.length ? [...new Set(allowedTypes)] : specialtyTypes(specialty);
  const uniqueTypes = [...new Set([auto || 'daily', ...types])];

  const isDiesel = form.report_type === 'diesel';
  const isEkedc = form.report_type === 'ekedc';
  const isSecurity = form.report_type === 'security_breach';
  const isIncident = form.report_type === 'incident';
  const isResidentsComplaint = form.report_type === 'residents_complaint';
  const isKpi = form.report_type === 'monthly_kpi';
  const isFacility = !!(specialty && specialty.toLowerCase().includes('facility'));

  const set = (patch: Partial<FormState>) => setForm((f) => ({ ...f, ...patch }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    upload.reset();
    try {
      const fd = new FormData();
      fd.append('report_type', form.report_type);
      if (!isFacility) fd.append('category', form.category);
      if (form.heading) fd.append('heading', form.heading);
      fd.append('content', form.content);
      if (form.report_date) fd.append('report_date', form.report_date);
      if (isDiesel) fd.append('diesel_liters_remaining', form.diesel_liters_remaining);
      if (isEkedc) fd.append('ekedc_units_remaining', form.ekedc_units_remaining);
      if (isSecurity || isIncident) {
        fd.append('severity', form.severity);
        if (form.security_location) fd.append('security_location', form.security_location);
        if (form.action_taken) fd.append('action_taken', form.action_taken);
      }
      if (isResidentsComplaint) {
        const validResidents = form.residents.filter((r) => r.name.trim());
        fd.append('resident_name', JSON.stringify(validResidents));
      }
      files.forEach((f) => fd.append('attachments', f));
      await upload.run('/reports', fd);
      onSubmitted();
      onClose();
      setForm({
        report_date: new Date().toISOString().slice(0, 10),
        report_type: auto || 'daily',
        category: 'general',
        heading: '',
        content: '',
        diesel_liters_remaining: '',
        ekedc_units_remaining: '',
        severity: '',
        security_location: '',
        action_taken: '',
        residents: [{ name: '', unit: '' }],
      });
      setFiles([]);
    } catch (err) {
      // useUpload already stores the server's message for the progress
      // component; this keeps the top-of-form alert in sync.
      setError((err as Error).message);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title={title}>
      {error && <Alert>{error}</Alert>}
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className={labelClass}>Report Date</label>
            <input type="date" className={inputClass} value={form.report_date} onChange={(e) => set({ report_date: e.target.value })} />
          </div>
          {showTypeSelect && uniqueTypes.length > 1 && (
            <div>
              <label className={labelClass}>Report Type</label>
              <select className={inputClass} value={form.report_type} onChange={(e) => set({ report_type: e.target.value as ReportType })}>
                {uniqueTypes.map((t) => <option key={t} value={t}>{REPORT_TYPE_LABELS[t]}</option>)}
              </select>
            </div>
          )}
        </div>

        {isFacility ? (
          <div>
            <label className={labelClass}>Report Heading</label>
            <input className={inputClass} value={form.heading} onChange={(e) => set({ heading: e.target.value })} placeholder="e.g. Generator servicing" />
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Category</label>
              <select className={inputClass} value={form.category} onChange={(e) => set({ category: e.target.value })}>
                {REPORT_CATEGORIES.map((c) => <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>)}
              </select>
            </div>
            <div>
              <label className={labelClass}>Report Heading</label>
              <input className={inputClass} value={form.heading} onChange={(e) => set({ heading: e.target.value })} placeholder="e.g. Generator servicing" />
            </div>
          </div>
        )}

        {isDiesel && (
          <div>
            <label className={labelClass}>Diesel Liters Remaining *</label>
            <input
              type="number" step="0.01" min="0" required className={inputClass}
              value={form.diesel_liters_remaining}
              onChange={(e) => set({ diesel_liters_remaining: e.target.value })}
              placeholder="e.g. 420"
            />
            <p className="text-xs text-gray-400 mt-1">Current diesel level remaining in the tank, in liters.</p>
          </div>
        )}

        {isEkedc && (
          <div>
            <label className={labelClass}>EKEDC Units Remaining *</label>
            <input
              type="number" step="0.01" min="0" required className={inputClass}
              value={form.ekedc_units_remaining}
              onChange={(e) => set({ ekedc_units_remaining: e.target.value })}
              placeholder="e.g. 1250"
            />
            <p className="text-xs text-gray-400 mt-1">Current prepaid electricity units remaining (kWh).</p>
          </div>
        )}

        {(isSecurity || isIncident) && (
          <>
            <div>
              <label className={labelClass}>Severity *</label>
              <select className={inputClass} required value={form.severity} onChange={(e) => set({ severity: e.target.value })}>
                <option value="">Select severity...</option>
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="critical">Critical</option>
              </select>
            </div>
            <div>
              <label className={labelClass}>Location</label>
              <input className={inputClass} value={form.security_location} onChange={(e) => set({ security_location: e.target.value })} placeholder="Where it occurred" />
            </div>
            <div>
              <label className={labelClass}>Action Taken</label>
              <textarea className={inputClass} rows={3} value={form.action_taken} onChange={(e) => set({ action_taken: e.target.value })} placeholder="What was done to contain or resolve it" />
            </div>
          </>
        )}

        {isResidentsComplaint && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <label className={labelClass}>Residents & Apartment Numbers *</label>
              {form.residents.length < 10 && (
                <button
                  type="button"
                  onClick={() => set({ residents: [...form.residents, { name: '', unit: '' }] })}
                  className="text-xs text-primary hover:underline"
                >
                  + Add Resident
                </button>
              )}
            </div>
            {form.residents.map((r, idx) => (
              <div key={idx} className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-2 items-end">
                <div>
                  <input
                    className={inputClass}
                    required={idx === 0}
                    value={r.name}
                    onChange={(e) => {
                      const updated = [...form.residents];
                      updated[idx] = { ...updated[idx], name: e.target.value };
                      set({ residents: updated });
                    }}
                    placeholder="Resident name"
                  />
                </div>
                <div>
                  <input
                    className={inputClass}
                    required={idx === 0}
                    value={r.unit}
                    onChange={(e) => {
                      const updated = [...form.residents];
                      updated[idx] = { ...updated[idx], unit: e.target.value };
                      set({ residents: updated });
                    }}
                    placeholder="Apartment number"
                  />
                </div>
                {form.residents.length > 1 && (
                  <button
                    type="button"
                    onClick={() => set({ residents: form.residents.filter((_, i) => i !== idx) })}
                    className="p-2 text-red-400 hover:text-red-600"
                    title="Remove"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}

        {isKpi && (
          <p className="text-sm text-gray-400 -mt-2">Enter your monthly KPI figures and performance notes below.</p>
        )}

        <div>
          <label className={labelClass}>{isDiesel ? 'Fuel Usage Notes *' : isEkedc ? 'Power Usage Notes *' : isSecurity || isIncident ? 'Incident Details *' : isResidentsComplaint ? 'Complaint Details *' : isKpi ? 'KPI Report Details *' : 'Report Details *'}</label>
          <textarea className={inputClass} rows={isSecurity || isIncident ? 4 : 7} required value={form.content} onChange={(e) => set({ content: e.target.value })} placeholder="Describe the report..." />
        </div>

        <div>
          <label className={labelClass}>Attachments (images, videos, PDFs, Excel, Word)</label>
          <input
            type="file"
            multiple
            accept="image/jpeg,image/png,image/gif,image/webp,video/*,.pdf,.doc,.docx,.xls,.xlsx,.csv"
            className={inputClass}
            onChange={(e) => setFiles(Array.from(e.target.files || []))}
          />
          {files.length > 0 && (
            <p className="text-xs text-gray-500 mt-1">
              {files.length} file{files.length === 1 ? '' : 's'} selected
            </p>
          )}
        </div>

        <UploadProgress
          percent={upload.percent}
          phase={upload.phase}
          error={upload.error}
          onCancel={upload.cancel}
        />

        <div className="flex justify-end space-x-3">
          <button type="button" onClick={onClose} className={secondaryBtn}>Cancel</button>
          <button type="submit" disabled={upload.busy} className={primaryBtn}>
            {upload.phase === 'uploading'
              ? `Uploading ${upload.percent}%`
              : upload.phase === 'processing'
                ? 'Processing...'
                : 'Submit Report'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
